import StatusBadge from '@/components/shared/Badge/Badge';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BillingCircle from '@/assets/icons/BillingCircle';
import {
  SubscriptionsService,
  type SubscriptionCycleItem,
  type SubscriptionInvoiceItem,
} from '@/services/subscriptions.service';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface SubscriptionBillingSectionProps {
  publicId: string;
}

function CycleBadge({ number }: { number: number }) {
  const { t } = useTranslation('signatures');

  return (
    <Badge
      variant="outline"
      className="border-zinc-700 bg-zinc-900/60 text-colorText font-medium shrink-0"
    >
      {t('details.billing.cycle_label', { number })}
    </Badge>
  );
}

function PeriodRange({
  start,
  end,
}: {
  start?: string | null;
  end?: string | null;
}) {
  if (!start && !end) {
    return <span className="text-muted text-sm">—</span>;
  }

  return (
    <div className="flex flex-col gap-0.5 min-w-[140px]">
      <span className="text-colorText text-sm">{start ?? '—'}</span>
      {end && (
        <span className="text-muted text-xs inline-flex items-center gap-1">
          <ArrowRight size={12} className="shrink-0" />
          {end}
        </span>
      )}
    </div>
  );
}

export default function SubscriptionBillingSection({
  publicId,
}: SubscriptionBillingSectionProps) {
  const { t } = useTranslation(['signatures', 'common']);
  const [invoicePage, setInvoicePage] = useState(1);
  const [cyclePage, setCyclePage] = useState(1);

  const { data: invoicesData, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['subscription-invoices', publicId, invoicePage],
    queryFn: () => SubscriptionsService.getSubscriptionInvoices(publicId, invoicePage),
  });

  const { data: cyclesData, isLoading: isLoadingCycles } = useQuery({
    queryKey: ['subscription-cycles', publicId, cyclePage],
    queryFn: () => SubscriptionsService.getSubscriptionCycles(publicId, cyclePage),
  });

  const invoiceTotal = invoicesData?.meta?.total;
  const cycleTotal = cyclesData?.meta?.total;

  const invoiceHeaders = [
    { title: t('details.billing.cycle'), key: 'cycle' },
    { title: t('details.billing.status'), key: 'status' },
    { title: t('details.billing.amount'), key: 'amount' },
    { title: t('details.billing.due_at'), key: 'due_at' },
    { title: t('details.billing.paid_at'), key: 'paid_at' },
    { title: '', key: 'actions' },
  ];

  const cycleHeaders = [
    { title: t('details.billing.cycle'), key: 'cycle' },
    { title: t('details.billing.status'), key: 'status' },
    { title: t('details.billing.amount'), key: 'amount' },
    { title: t('details.billing.period_start'), key: 'period' },
    { title: '', key: 'actions' },
  ];

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-1">
          <BillingCircle />
          <h2 className="text-colorText font-semibold">
            {t('details.billing.title')}
          </h2>
        </div>
        <p className="text-muted text-sm mt-1">{t('details.billing.subtitle')}</p>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="inline-flex h-auto w-fit gap-1 rounded-lg bg-zinc-900/80 p-1 border border-zinc-800">
          <TabsTrigger
            value="invoices"
            className="rounded-md px-4 py-2 text-sm data-[state=active]:bg-zinc-800 data-[state=active]:text-colorText data-[state=active]:shadow-sm"
          >
            {t('details.tabs.invoices')}
            {invoiceTotal != null && (
              <span className="ml-2 text-xs text-muted">({invoiceTotal})</span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="cycles"
            className="rounded-md px-4 py-2 text-sm data-[state=active]:bg-zinc-800 data-[state=active]:text-colorText data-[state=active]:shadow-sm"
          >
            {t('details.tabs.cycles')}
            {cycleTotal != null && (
              <span className="ml-2 text-xs text-muted">({cycleTotal})</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <div className="rounded-lg border border-zinc-800 overflow-hidden">
            <TableListCustom
              headers={invoiceHeaders}
              isLoadingData={isLoadingInvoices}
              data={{
                data: invoicesData?.data ?? [],
                meta: invoicesData?.meta,
              }}
              currentPage={invoicePage}
              setCurrentPage={setInvoicePage}
              emptyTitleMessage={t('details.billing.empty_invoices')}
            >
              {(invoicesData?.data ?? []).map((invoice: SubscriptionInvoiceItem) => (
                <TableRow
                  key={invoice.public_id}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40"
                >
                  <TableCell>
                    <CycleBadge number={invoice.cycle_number} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={invoice.status}
                      text={t(`details.billing.invoice_status.${invoice.status}`, {
                        defaultValue: invoice.status,
                      })}
                    />
                  </TableCell>
                  <TableCell>
                    <span className="text-colorText font-semibold text-sm block">
                      {invoice.amount_formatted}
                    </span>
                    {invoice.subscription_amount_formatted && invoice.shipping ? (
                      <span className="text-muted text-xs block">
                        {invoice.shipping.is_free
                          ? t('details.invoice_shipping_free', {
                              subscription: invoice.subscription_amount_formatted,
                            })
                          : t('details.invoice_shipping_split', {
                              subscription: invoice.subscription_amount_formatted,
                              shipping: invoice.shipping.amount_formatted,
                            })}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <span className="text-colorText text-sm">
                      {invoice.due_at_formatted ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted text-sm">
                      {invoice.paid_at_formatted ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {invoice.mundpay_order_id && (
                      <Link
                        to={`/sales/sales-details/${invoice.mundpay_order_id}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 px-3 py-1.5 text-xs font-medium text-interactive hover:bg-zinc-900 transition-colors"
                      >
                        {t('details.billing.view_order')}
                        <ExternalLink size={12} />
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableListCustom>
          </div>
        </TabsContent>

        <TabsContent value="cycles" className="mt-4">
          <div className="rounded-lg border border-zinc-800 overflow-hidden">
            <TableListCustom
              headers={cycleHeaders}
              isLoadingData={isLoadingCycles}
              data={{
                data: cyclesData?.data ?? [],
                meta: cyclesData?.meta,
              }}
              currentPage={cyclePage}
              setCurrentPage={setCyclePage}
              emptyTitleMessage={t('details.billing.empty_cycles')}
            >
              {(cyclesData?.data ?? []).map((cycle: SubscriptionCycleItem) => (
                <TableRow
                  key={`${cycle.cycle_number}-${cycle.period_start_formatted}`}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40"
                >
                  <TableCell>
                    <CycleBadge number={cycle.cycle_number} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={cycle.status}
                      text={t(`details.billing.cycle_status.${cycle.status}`, {
                        defaultValue: cycle.status,
                      })}
                    />
                  </TableCell>
                  <TableCell>
                    <span className="text-colorText font-semibold text-sm">
                      {cycle.amount_formatted}
                    </span>
                  </TableCell>
                  <TableCell>
                    <PeriodRange
                      start={cycle.period_start_formatted}
                      end={cycle.period_end_formatted}
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))}
            </TableListCustom>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
